import { Component, Input, OnInit } from '@angular/core';
import { IDateFieldSpecs, IFieldSpecs } from "libraries/module/formbuilder/fields/interfaces/field-specs";
import { Yii } from "libraries/interfaces/Yii";
import { FormArray, FormControl, FormGroup } from "@angular/forms";
import { SearchFormHelperService } from "app_sasha/shared/services/search-form-helper/search-form-helper.service";
import { TicketingReportDomainService } from "app_sasha/pages/ticketing-report/ticketing-report-domain.service";
import { ICodeName } from "libraries/service/resources/model/shared/ICodeName";
import { IComboboxOption } from "libraries/module/formbuilder/fields/combobox-field/combobox-field.component";
import { BtnStyleTypes } from "libraries/ui_button/btn.component";
import { IconDefinition } from "@fortawesome/free-regular-svg-icons";
import { faCircle, faMinus, faPlus, faQuestion } from "@fortawesome/free-solid-svg-icons";
import { TicketSearchFormMapperService } from "../ticketing-search-form-mapper.service";
import { TicketingReportSearchBooking } from "@afidium/ts-facade";
import { IDescriptionLabels } from "libraries/module/formbuilder/fields/form-field/components/field-description/field-description.component";

enum ETicketSearchBookingFields {
    references = 'references',
    creation = 'creation',
    creationMin = 'creationMin',
    creationMax = 'creationMax',
    begin = 'begin',
    beginMin = 'beginMin',
    beginMax = 'beginMax',
    customFields = 'customFields',
}

export interface ITicketSearchFormBookingOutput {
    [ETicketSearchBookingFields.references]: string[];
    [ETicketSearchBookingFields.creation]: {
        [ETicketSearchBookingFields.creationMin]?: Date;
        [ETicketSearchBookingFields.creationMax]?: Date;
    };
    [ETicketSearchBookingFields.begin]: {
        [ETicketSearchBookingFields.beginMin]?: Date;
        [ETicketSearchBookingFields.beginMax]?: Date;
    };
    [ETicketSearchBookingFields.customFields]?: ITicketSearchFormBookingOutputCustomField[];
}

export interface ITicketSearchFormBookingOutputCustomField {
    code: string;
    value: string;
}

interface IBookingConfig {
    customFieldsOptions: IComboboxOption[];
    // [ETicketSearchBookingFields.customFields]: IFieldSpecs[];
    [ETicketSearchBookingFields.references]: IFieldSpecs;
    [ETicketSearchBookingFields.creationMin]: IDateFieldSpecs;
    [ETicketSearchBookingFields.creationMax]: IDateFieldSpecs;
    [ETicketSearchBookingFields.beginMin]: IDateFieldSpecs;
    [ETicketSearchBookingFields.beginMax]: IDateFieldSpecs;
}

class Wording {
    public readonly booking: string = Yii.t('wui-sasha-js', 'Dossiers');
    public readonly [ETicketSearchBookingFields.references]: string = Yii.t('wui-sasha-js', 'Références');
    public readonly [ETicketSearchBookingFields.creationMin]: string = Yii.t('wui-sasha-js', 'Création min');
    public readonly [ETicketSearchBookingFields.creationMax]: string = Yii.t('wui-sasha-js', 'Création max');
    public readonly [ETicketSearchBookingFields.beginMin]: string = Yii.t('wui-sasha-js', 'Départ min');
    public readonly [ETicketSearchBookingFields.beginMax]: string = Yii.t('wui-sasha-js', 'Départ max');
    public readonly [ETicketSearchBookingFields.customFields]: string = Yii.t('wui-sasha-js', 'Champ personnalisé');

    public readonly referenceDescription: IDescriptionLabels = {
        main: Yii.t('wui-sasha-js', 'Recherche un dossier à partir d\'une de ses références qui peut être :'),
        detailList: [
            Yii.t('wui-sasha-js', 'Un Numéro de PNR ;'),
            Yii.t('wui-sasha-js', 'Une Référence de dossier mid-office ;'),
            Yii.t('wui-sasha-js', 'Une référence de dossier de compagnie aérienne ;'),
            Yii.t('wui-sasha-js', '...'),
        ],
        syntaxHelp: [
            Yii.t('wui-sasha-js', 'NOT ou un tiret au début d\'un mot pour rechercher des valeurs qui ne contiennent pas ce mot ;'),
            Yii.t('wui-sasha-js', '* pour rechercher un texte partiel ;'),
            Yii.t('wui-sasha-js', 'AND pour combiner des expressions.'),
        ]
    };
    public readonly dateElements: Partial<IDescriptionLabels> = {
        detailIntroduction: Yii.t('wui-sasha-js', 'Pour des recherches plus précises sur les dossiers aériens, utiliser les critères associés au vols. Ce champ supporte les expressions de date :'),
        detailList: [
            Yii.t('wui-sasha-js', 'aujourd\'hui : = ;'),
            Yii.t('wui-sasha-js', 'début du mois : < ;'),
            Yii.t('wui-sasha-js', 'fin du mois : > ;'),
            Yii.t('wui-sasha-js', 'dans deux semaines : +2w ;'),
            Yii.t('wui-sasha-js', 'dans trois mois : +3m .'),
        ],
    };
    public readonly creaMinDescription: IDescriptionLabels = {
        main: Yii.t('wui-sasha-js', 'Pour indiquer la date à partir de laquelle les dossiers associés aux billets ont été créés.'),
        ...this.dateElements,
    };
    public readonly creaMaxDescription: IDescriptionLabels = {
        main: Yii.t('wui-sasha-js', 'Pour indiquer la date jusqu\'à laquelle les dossiers associés aux billets ont été créés.'),
        ...this.dateElements,
    };
    public readonly beginMinDescription: IDescriptionLabels = {
        main: Yii.t('wui-sasha-js', 'Pour indiquer la date de départ minimale du voyage dans le dossier (qui peut être différente ce celle du billet) sur laquelle effectuer la recherche. Pour des recherches plus précises sur les dossiers aériens, utiliser les critères associés au vols.'),
        ...this.dateElements,
    };
    public readonly beginMaxDescription: IDescriptionLabels = {
        main: Yii.t('wui-sasha-js', 'Pour indiquer la date de départ butoir du voyage dans le dossier (qui peut être différente ce celle du billet) sur laquelle effectuer la recherche. Pour des recherches plus précises sur les dossiers aériens, utiliser les critères associés au vols.'),
        ...this.dateElements,
    };
    public readonly customDescription: IDescriptionLabels = {
        main: Yii.t('wui-sasha-js', 'Recherche sur les champs personnalisés du dossier GDS associé au billet. '),
        detailIntroduction: Yii.t('wui-sasha-js', 'Les codes de ces champs sont spécifiques à chaque client.\n Les valeurs associées à ces champs ne sont pas formattées.')

    };
}

@Component({
    selector: 'ticketing-booking-fieldset',
    template: `
        <collapsible [title]="wording.booking" [open]="bookingOpen" *ngIf="bookingConfig">
            <div class="col-to-row-s-tb sep-top">
                <div class="flex-1">
                    <chips [fieldConfig]="bookingConfig[bookingField.references]"></chips>
                </div>
            </div>
            <div class="col-to-row-s-tb sep-top">
                <div class="flex-1 sep-right">
                    <date-field
                            [fieldConfig]="bookingConfig[bookingField.creationMin]"></date-field>
                </div>
                <div class="flex-1">
                    <date-field
                            [fieldConfig]="bookingConfig[bookingField.creationMax]"></date-field>
                </div>
            </div>
            <div class="col-to-row-s-tb sep-top">
                <div class="flex-1 sep-right">
                    <date-field [fieldConfig]="bookingConfig[bookingField.beginMin]"></date-field>
                </div>
                <div class="flex-1">
                    <date-field [fieldConfig]="bookingConfig[bookingField.beginMax]"></date-field>
                </div>
            </div>
            <div *ngFor="let customFieldValue of customFieldsValues; let i = index; let l = last">
                <div class="sep-top btn-title custom-container">
                    {{wording[bookingField.customFields]}} {{i + 1}}
                    <fa-layers (click)="showDescription = !showDescription">
                        <fa-icon [icon]="iconCircle" class="blue"></fa-icon>
                        <fa-icon [icon]="iconQuestion" class="white" transform="shrink-5"></fa-icon>
                    </fa-layers>
                    <field-description [description]="wording.customDescription"
                                       *ngIf="showDescription"
                                       (clickOutside)="showDescription = !showDescription"></field-description>
                </div>
                <div class="col-to-row-s-tb btn-line">
                    <div class="flex-1 sep-right ">
                        <combobox-field [fieldConfig]="myFormGroup | fieldConfigGetter:i:'code'"
                                        [items]="bookingConfig.customFieldsOptions"></combobox-field>
                    </div>
                    <div class="flex-1">
                        <text [fieldConfig]="myFormGroup | fieldConfigGetter:i:'value'"></text>
                    </div>
                    <div class="flex-1">
                        <btn [type]="btnType" *ngIf="customFieldsValues.length !==1" (onClick)="remove(i)">
                            <div class="icon">
                                <fa-icon [icon]="iconMinus"></fa-icon>
                            </div>
                        </btn>
                        <btn [type]="btnType" *ngIf="l" (onClick)="add()">
                            <div class="icon">
                                <fa-icon [icon]="iconPlus"></fa-icon>
                            </div>
                        </btn>
                    </div>
                </div>
            </div>
        </collapsible>
    `,
    styles: [`
        .btn-line {
            align-items: stretch;
        }

        .btn-title {
            padding-bottom: .2rem;
        }

        fa-layers {
            padding: 0 .4rem;
            cursor: pointer;
            line-height: 1.4rem;
            transform: scale(.8) translate(4px, 3px);
        }

        fa-icon.blue {
            color: #337ab7;
        }

        fa-icon.blue:hover {
            color: #414141;
        }

        fa-icon.white {
            color: white;
        }

        .custom-container {
            position: relative;
        }

        field-description {
            position: absolute;
            z-index: 2;
            width: 100%;
            top: 1.2rem;
            left: 0;
        }
    `]
})
export class TicketingBookingFieldsetComponent implements OnInit {
    @Input()
    public myFormGroup: FormGroup;
    @Input()
    public apiRequestBooking: TicketingReportSearchBooking;

    public readonly bookingField: typeof ETicketSearchBookingFields = ETicketSearchBookingFields;
    public readonly wording: Wording = new Wording();

    public bookingOpen: boolean = true;
    public bookingConfig: IBookingConfig;
    public isReady: boolean;
    public btnType: BtnStyleTypes = BtnStyleTypes.actionLink;
    public customFieldsValues: ITicketSearchFormBookingOutputCustomField[];
    public iconPlus: IconDefinition = faPlus;
    public iconMinus: IconDefinition = faMinus;
    public iconQuestion: IconDefinition = faQuestion;
    public iconCircle: IconDefinition = faCircle;


    public showDescription: boolean = false;

    private newCustomField: { [key: string]: FormControl } = {
        code: new FormControl(),
        value: new FormControl(),
    };

    constructor(
        private readonly helperService: SearchFormHelperService,
        private readonly ticketingReportDomainService: TicketingReportDomainService,
        private readonly ticketSearchFormMapperService: TicketSearchFormMapperService
    ) {
    }

    public ngOnInit(): void {
        this.myFormGroup.valueChanges.subscribe(
            (value: ITicketSearchFormBookingOutput): void => {
                if (value[ETicketSearchBookingFields.customFields]) {
                    if ((!this.customFieldsValues) || (this.customFieldsValues.length !== value[ETicketSearchBookingFields.customFields].length)) {
                        this.customFieldsValues = value[ETicketSearchBookingFields.customFields];
                    }
                }
            }
        );
        this.ticketingReportDomainService.getCustomFields()
            .subscribe(
                {
                    next: (customFields: ICodeName[]): void => {
                        this.createBookingFormControls();
                        this.createBookingConfig(customFields);
                        this.patchForm();
                        this.isReady = true;
                    }
                }
            );
    }

    public patchForm(): void {
        const customFieldFrom: FormArray = this.myFormGroup.get(ETicketSearchBookingFields.customFields) as FormArray;
        if (!this.apiRequestBooking) {
            this.myFormGroup.reset({});
            customFieldFrom.clear();
            this.add();
        } else {
            const newFormValue: ITicketSearchFormBookingOutput = this.ticketSearchFormMapperService.mapApiBookingToFormBooking(this.apiRequestBooking);
            this.myFormGroup.get(ETicketSearchBookingFields.references).setValue(newFormValue[ETicketSearchBookingFields.references]);
            this.myFormGroup.get(ETicketSearchBookingFields.creation).setValue(newFormValue[ETicketSearchBookingFields.creation]);
            this.myFormGroup.get(ETicketSearchBookingFields.begin).setValue(newFormValue[ETicketSearchBookingFields.begin]);
            customFieldFrom.clear();
            if (newFormValue[ETicketSearchBookingFields.customFields]?.length) {
                newFormValue[ETicketSearchBookingFields.customFields].forEach(
                    (newCustomFieldValue: ITicketSearchFormBookingOutputCustomField): void => {
                        customFieldFrom.push(new FormGroup({
                            code: new FormControl(newCustomFieldValue.code),
                            value: new FormControl(newCustomFieldValue.value),
                        }));
                    }
                );
            } else {
                this.add();
            }
        }
    }

    public createBookingFormControls(): void {

        this.myFormGroup.setControl(ETicketSearchBookingFields.references, new FormControl());
        this.myFormGroup.setControl(ETicketSearchBookingFields.creation, new FormGroup({
            [ETicketSearchBookingFields.creationMin]: new FormControl(),
            [ETicketSearchBookingFields.creationMax]: new FormControl(),
        }));
        this.myFormGroup.setControl(ETicketSearchBookingFields.begin, new FormGroup({
            [ETicketSearchBookingFields.beginMin]: new FormControl(),
            [ETicketSearchBookingFields.beginMax]: new FormControl(),
        }));
        this.myFormGroup.setControl(ETicketSearchBookingFields.customFields, new FormArray([
            new FormGroup({
                code: new FormControl(),
                value: new FormControl(),
            })
        ]));
    }

    public createBookingConfig(customFields: ICodeName[]): void {
        this.bookingConfig = {
            customFieldsOptions: customFields.map((codeName: ICodeName): IComboboxOption => ({
                value: codeName.code,
                label: codeName.name
            })),

            [ETicketSearchBookingFields.references]: this.helperService.createSimpleConfig(
                ETicketSearchBookingFields.references,
                this.myFormGroup,
                this.wording.references,
                this.wording.referenceDescription),
            [ETicketSearchBookingFields.creationMin]: this.helperService.createDateConfig(
                ETicketSearchBookingFields.creationMin,
                this.myFormGroup.get(ETicketSearchBookingFields.creation) as FormGroup,
                this.wording.creationMin,
                true,
                this.wording.creaMinDescription),
            [ETicketSearchBookingFields.creationMax]: this.helperService.createDateConfig(
                ETicketSearchBookingFields.creationMax,
                this.myFormGroup.get(ETicketSearchBookingFields.creation) as FormGroup,
                this.wording.creationMax,
                true,
                this.wording.creaMaxDescription
            ),
            [ETicketSearchBookingFields.beginMin]: this.helperService.createDateConfig(
                ETicketSearchBookingFields.beginMin,
                this.myFormGroup.get(ETicketSearchBookingFields.begin) as FormGroup,
                this.wording.beginMin,
                false,
                this.wording.beginMinDescription
            ),
            [ETicketSearchBookingFields.beginMax]: this.helperService.createDateConfig(
                ETicketSearchBookingFields.beginMax,
                this.myFormGroup.get(ETicketSearchBookingFields.begin) as FormGroup,
                this.wording.beginMax,
                false,
                this.wording.beginMaxDescription,
            ),
        };
    }

    public remove(index: number): void {
        (this.myFormGroup.get(ETicketSearchBookingFields.customFields) as FormArray)
            .removeAt(index);
    }

    public add(): void {
        (this.myFormGroup.get(ETicketSearchBookingFields.customFields) as FormArray).push(
            new FormGroup({
                code: new FormControl(),
                value: new FormControl(),
            })
        );
    }

}
